import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  Edit,
  Delete,
  Add,
  LocalOffer,
} from '@mui/icons-material';
import API from "../../../utils/API";
import utils from "../../../utils/utils";
import { useLocation } from "react-router-dom";

export default function Discounts(props) {
  const location = useLocation();
  const initDiscount = {
    discount_type: "cash_amount",
    amount: 0,
    name: "",
    discount_code: "",
    id: 0
  }

  const [discounts, setDiscounts] = useState([])
  const [activeDiscount, setActiveDiscount] = useState(initDiscount)
  const [confirmDiscountDelete, setConfirmDiscountDelete] = useState(false)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (props.showID) {
      reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.showID])

  function reset() {
    const params = new URLSearchParams(location.search);
		const showID = params.get("showID");
    API.getDiscounts(showID)
      .then(res => {
        setDiscounts(res.data)
      })
  }

  function handleDiscountCodeChange(e) {
    setActiveDiscount({ ...activeDiscount, [e.target.name]: e.target.value })
  }

  function createDiscountCode(e) {
    e.preventDefault();
    setLoading(true);
    
    if (activeDiscount.id === 0) {
      const req = activeDiscount
      req["show"] = props.showID
      API.createDiscountCode(req)
        .then(res => {
          utils.sendAlert("Discount Code Created", "success");
          reset();
          setActiveDiscount(initDiscount);
        })
        .catch(err => {
          utils.sendAlert("Error creating discount code", "error");
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      API.editDiscount(activeDiscount)
        .then(res => {
          utils.sendAlert("Discount Code Updated", "success");
          reset();
          setActiveDiscount(initDiscount);
        })
        .catch(err => {
          utils.sendAlert("Error updating discount code", "error");
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }

  function deleteDiscount() {
    API.deleteDiscount(activeDiscount.id)
      .then(res => {
        utils.sendAlert("Discount Deleted", "success")
        setConfirmDiscountDelete(false)
        reset()
        setActiveDiscount(initDiscount)
      })
      .catch(err => {
        utils.sendAlert("Error deleting discount", "error")
      })
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">
          Discount Codes
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setActiveDiscount(initDiscount)}
        >
          Create New Discount
        </Button>
      </Box>

      {/* Discounts Table */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 0 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Discount Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Discount Code</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Discount Amount</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Uses</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {discounts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography color="text.secondary">
                        No discount codes created yet
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  discounts.map(discount => (
                    <TableRow key={discount.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {discount.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={discount.discount_code} 
                          variant="outlined" 
                          color="primary"
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {discount.discount_type === "cash_amount" ? `$${discount.amount}` : `${discount.amount}%`}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {discount.uses || 0}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton
                            size="small"
                            onClick={() => setActiveDiscount(discount)}
                            color="primary"
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setActiveDiscount(discount);
                              setConfirmDiscountDelete(true);
                            }}
                            color="error"
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Create/Edit Form */}
      <Card>
        <CardContent>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            {activeDiscount.id !== 0 ? `Edit ${activeDiscount.name}` : "Create New Discount Code"}
          </Typography>
          
          <Box component="form" onSubmit={createDiscountCode} sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                fullWidth
                label="Discount Name"
                name="name"
                value={activeDiscount.name}
                onChange={handleDiscountCodeChange}
                required
                placeholder="e.g., Early Bird Special"
              />
              <TextField
                fullWidth
                label="Discount Code"
                name="discount_code"
                value={activeDiscount.discount_code}
                onChange={handleDiscountCodeChange}
                required
                placeholder="e.g., EARLYBIRD"
                sx={{ textTransform: 'uppercase' }}
              />
            </Box>
            
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Discount Type</InputLabel>
                <Select
                  name="discount_type"
                  value={activeDiscount.discount_type}
                  onChange={handleDiscountCodeChange}
                  label="Discount Type"
                >
                  <MenuItem value="cash_amount">$ Amount</MenuItem>
                  <MenuItem value="percentage">% Percentage</MenuItem>
                </Select>
              </FormControl>
              
              <TextField
                fullWidth
                label="Discount Amount"
                name="amount"
                type="number"
                value={activeDiscount.amount}
                onChange={handleDiscountCodeChange}
                required
                placeholder="0"
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Box>
            
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={() => setActiveDiscount(initDiscount)}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                sx={{ minWidth: '150px' }}
              >
                {loading ? 'Saving...' : (activeDiscount.id === 0 ? 'Create Discount Code' : 'Update Discount Code')}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={confirmDiscountDelete} 
        onClose={() => setConfirmDiscountDelete(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Confirm Discount Deletion
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Are you sure you want to delete "{activeDiscount.name}"? This will not impact customers who have already used it.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDiscountDelete(false)}>
            Cancel
          </Button>
          <Button 
            onClick={deleteDiscount} 
            color="error"
            variant="contained"
          >
            Yes, Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}